import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from './common/Header';
import Footer from './common/Footer';

const DoNotSellPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    confirmOptOut: false,
  });

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Initialize EmailJS with your public key
      emailjs.init('1YES4s61T8zyFqTd_');

      // Prepare template parameters for CCPA request
      const templateParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        timestamp: new Date().toLocaleString(),
      };

      // Send CCPA opt-out request email
      const result = await emailjs.send(
        'service_y8i43dv', // Service ID
        'template_u2nboh7', // CCPA Template ID
        templateParams,
      );

      setFormSubmitted(true);
    } catch (error) {
      console.error('Error sending CCPA request:', error);
      alert(
        'Sorry, there was an error submitting your request. Please try again or email us directly at datarequest@electricnow.tv',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Header />

        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center">
            <div className="w-24 h-24 bg-[#2f6eb0] rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✅</span>
            </div>
            <h1 className="text-4xl font-bold mb-4 text-[#2f6eb0]">
              Request Submitted Successfully
            </h1>
            <p className="text-gray-300 mb-8 leading-relaxed">
              Your "Do Not Sell My Data" request has been submitted. We will process your request
              within 15 business days and send you a confirmation email.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Return Home
              </Link>
              <Link
                to="/privacy-policy"
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                View Privacy Policy
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
            Do Not Sell My Personal Information
          </h1>
          <p className="text-gray-400">
            Exercise your right to opt out of the sale of your personal information
          </p>
        </div>

        {/* Quick Info */}
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 mb-12">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xl">🛡️</span>
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2 text-red-400">Your Right to Opt Out</h2>
              <p className="text-gray-300 leading-relaxed">
                Under the California Consumer Privacy Act (CCPA), you have the right to opt out of
                the sale of your personal information. This form allows you to exercise that right
                quickly and easily.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          {/* What This Means */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">What Does "Sale" Mean?</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                Under the CCPA, "sale" has a broad definition that includes sharing personal
                information with third parties for monetary or other valuable consideration. This
                can include:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-semibold text-orange-400 mb-2">Examples of "Sale":</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Sharing data with advertising partners</li>
                    <li>Providing information to analytics companies</li>
                    <li>Exchanging data for services or benefits</li>
                    <li>Cross-context behavioral advertising</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#9ec6f0] mb-2">Not Considered "Sale":</h4>
                  <ul className="text-gray-300 text-sm list-disc list-inside space-y-1">
                    <li>Sharing with service providers</li>
                    <li>Disclosures for legal compliance</li>
                    <li>Transfers in business transactions</li>
                    <li>Sharing with your consent for specific purposes</li>
                  </ul>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed">
                By submitting this form, you are requesting that we stop any activities that would
                be considered a "sale" of your personal information under the CCPA.
              </p>
            </div>
          </section>

          {/* Opt-Out Form */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Submit Your Opt-Out Request</h2>
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-300 mb-2"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2f6eb0] focus:border-transparent"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-300 mb-2"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2f6eb0] focus:border-transparent"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2f6eb0] focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                  <p className="text-gray-400 text-sm mt-1">
                    We'll use this email to confirm your opt-out request and send you a
                    confirmation.
                  </p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="confirmOptOut"
                      name="confirmOptOut"
                      required
                      checked={formData.confirmOptOut}
                      onChange={handleInputChange}
                      className="mt-1 w-4 h-4 text-[#2f6eb0] bg-gray-600 border-gray-500 rounded focus:ring-[#2f6eb0] focus:ring-2"
                    />
                    <label htmlFor="confirmOptOut" className="text-sm text-gray-300">
                      I confirm that I want to opt out of the sale of my personal information. I
                      understand that this request applies to ElectricNOW and its affiliated
                      companies. I have read and understand the{' '}
                      <button
                        onClick={() => window.open('/privacy-policy', '_blank')}
                        className="text-[#9ec6f0] hover:text-[#b3cfed] underline bg-transparent border-none cursor-pointer"
                      >
                        Privacy Policy
                      </button>{' '}
                      and my rights under the{' '}
                      <button onClick={() => window.open('/ccpa', '_blank')} className="text-[#9ec6f0] hover:text-[#b3cfed] underline bg-transparent border-none cursor-pointer">
                        California Consumer Privacy Act
                      </button>
                      .
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-4 rounded-lg font-semibold transition-colors text-lg ${
                    isSubmitting ? 'bg-gray-600 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                  } text-white`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting Request...</span>
                    </div>
                  ) : (
                    'Submit Opt-Out Request'
                  )}
                </button>
              </form>
            </div>
          </section>

          {/* What Happens Next */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">What Happens Next?</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📝</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-blue-400">Request Received</h4>
                  <p className="text-gray-300 text-sm">
                    We receive and log your opt-out request immediately
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">⚙️</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-yellow-400">Processing</h4>
                  <p className="text-gray-300 text-sm">
                    We update our systems to honor your opt-out preference
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">✅</span>
                  </div>
                  <h4 className="font-semibold mb-2 text-[#9ec6f0]">Confirmation</h4>
                  <p className="text-gray-300 text-sm">
                    You receive email confirmation within 15 business days
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Important Information</h2>
            <div className="space-y-6">
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3 text-blue-400">Scope of This Request</h3>
                <p className="text-gray-300 leading-relaxed mb-3">
                  This opt-out request applies to:
                </p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>ElectricNOW and all affiliated companies</li>
                  <li>All current and future "sales" as defined by the CCPA</li>
                  <li>All personal information categories we collect about you</li>
                </ul>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3 text-yellow-400">
                  What This Doesn't Affect
                </h3>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Opting out of sales will not affect:
                </p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>Your ability to use ElectricNOW services</li>
                  <li>Sharing with service providers necessary for our operations</li>
                  <li>Disclosures required by law or for legal proceedings</li>
                  <li>Transfers in connection with business transactions</li>
                </ul>
              </div>

              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3 text-[#9ec6f0]">Your Other Rights</h3>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Remember, you also have other rights under the CCPA:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/ccpa"
                    className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Right to Know
                  </Link>
                  <Link
                    to="/ccpa"
                    className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Right to Delete
                  </Link>
                  <Link
                    to="/ccpa"
                    className="bg-[#2f6eb0] hover:bg-[#3d7fcb] text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    Right to Portability
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Additional Resources */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Additional Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">Learn More</h3>
                <div className="space-y-2">
                  <Link
                    to="/privacy-policy"
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    Full Privacy Policy
                  </Link>
                  <Link to="/ccpa" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    California Privacy Rights (CCPA)
                  </Link>
                  <Link
                    to="/terms-of-use"
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    Terms of Use
                  </Link>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-[#9ec6f0]">Need Help?</h3>
                <div className="space-y-2">
                  <a
                    href="mailto:datarequest@electricnow.tv"
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    Privacy Team Email
                  </a>
                  <Link to="/contact" className="block text-[#9ec6f0] hover:text-[#b3cfed] underline">
                    Contact Form
                  </Link>
                  <a
                    href="https://oag.ca.gov/privacy/ccpa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[#9ec6f0] hover:text-[#b3cfed] underline"
                  >
                    California AG CCPA Info
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DoNotSellPage;
